import { useEffect, useState } from "react"
import { CarRouteConfig } from "@/api/adHocCommands"
import { cn } from "@/lib/utils"
import { ChevronDown, ChevronUp, Fuel, Ticket, Truck } from "lucide-react"
import { Toll } from "@/reducers/routes2gSlice"
import { getCountryFlag, getCountryName } from "@/components/maps/mapUtils"
import { Input } from "@/components/ui/input"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"
import { optionLabel } from "@/components/maps/carRouteOptions"

interface CollapsibleVehicleTollInfoProps {
  config: CarRouteConfig
  tolls?: Toll[]
  fuel?: number
  className?: string
  /**
   * When false, all fuel-related UI is hidden (estimated consumption badge,
   * fuel section, and the l/100km input). Defaults to `true`.
   * Set to `false` for non-HERE routing providers that do not return fuel data.
   */
  showFuel?: boolean
  /**
   * When provided, the average fuel consumption (l/100km) becomes editable.
   * Called with the new value once the user commits it (blur / Enter), or
   * with `undefined` when the field is cleared.
   */
  onFuelConsumptionChange?: (value: string | undefined) => void
}

// Matches CarConfigSelector and the GPS backend validation (0 < value <= 100).
export const MAX_FUEL_CONSUMPTION = 100

// Normalises a committed input value: `undefined` for empty / non-numeric /
// non-positive (no consumption known), otherwise the value clamped to the
// allowed range, as a string like the rest of CarRouteConfig.
export const normalizeFuelConsumption = (
  value: string | number | undefined,
): string | undefined => {
  if (value === undefined || value === "") return undefined
  const numeric = typeof value === "number" ? value : parseFloat(value)
  if (isNaN(numeric) || numeric <= 0) return undefined
  return String(Math.min(numeric, MAX_FUEL_CONSUMPTION))
}

// Currency formatting function
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat(getIntlLocale(undefined), {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return formatter.format(amount)
}

// Group tolls by country and currency
const groupTollsByCountry = (tolls: Toll[]) => {
  return tolls.reduce<Record<string, Record<string, number>>>((acc, toll) => {
    if (!acc[toll.country_code]) {
      acc[toll.country_code] = {}
    }
    if (!acc[toll.country_code][toll.currency]) {
      acc[toll.country_code][toll.currency] = 0
    }
    acc[toll.country_code][toll.currency] += toll.price
    return acc
  }, {})
}

// Calculate total by currency
const getTotalByCurrency = (tolls: Toll[]) => {
  return tolls.reduce<Record<string, number>>((acc, toll) => {
    if (!acc[toll.currency]) {
      acc[toll.currency] = 0
    }
    acc[toll.currency] += toll.price
    return acc
  }, {})
}

export default function CollapsibleVehicleTollInfo({
  config,
  tolls,
  fuel,
  className,
  showFuel = true,
  onFuelConsumptionChange,
}: CollapsibleVehicleTollInfoProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  // Draft of the fuel consumption input; committed to the parent on blur/Enter
  // so a single keystroke sequence ("28.5") triggers one route recalculation.
  const [fuelDraft, setFuelDraft] = useState(config.fuelConsumption ?? "")
  useEffect(() => {
    setFuelDraft(config.fuelConsumption ?? "")
  }, [config.fuelConsumption])

  const commitFuelDraft = () => {
    if (!onFuelConsumptionChange) return
    const normalized = normalizeFuelConsumption(fuelDraft)
    // Reflect clamping / rejection in the field right away
    setFuelDraft(normalized ?? "")
    if (normalized !== config.fuelConsumption) {
      onFuelConsumptionChange(normalized)
    }
  }

  const formatValue = (value: string | undefined, unit: string) => {
    if (!value) return "—"
    return `${value}${unit}`
  }

  const formatString = (value: string | undefined) => {
    if (!value) return "—"
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  // Known option values ("truck", "none") get their translated label.
  const formatOption = (value: string | undefined) => {
    if (!value) return "—"
    return optionLabel(t, { label: formatString(value), value })
  }

  // Format weight for collapsed view (convert kg to tons if needed)
  const formatWeightCompact = (weightKg: string | undefined) => {
    if (!weightKg) return ""
    const weight = parseFloat(weightKg)
    if (weight >= 1000) {
      return `${(weight / 1000).toFixed(0)}t`
    }
    return `${weight}kg`
  }

  // Format toll for compact display (e.g., "12 EUR")
  const formatTollCompact = (amount: number, currency: string): string => {
    return `${Math.round(amount)} ${currency}`
  }

  // Get total toll cost for collapsed view
  const getTotalTollDisplay = () => {
    if (!tolls || tolls.length === 0) return null
    const totalByCurrency = getTotalByCurrency(tolls)
    return Object.entries(totalByCurrency)
      .map(([currency, amount]) => formatTollCompact(amount, currency))
      .join(" + ")
  }

  const totalTollDisplay = getTotalTollDisplay()
  const tollsByCountry = tolls ? groupTollsByCountry(tolls) : {}
  const totalByCurrency = tolls ? getTotalByCurrency(tolls) : {}

  // Collapsed view summary
  const dimensionsSummary = [
    config.length ? `${config.length}m` : null,
    config.width ? `${config.width}m` : null,
    config.height ? `${config.height}m` : null,
  ]
    .filter(Boolean)
    .join(" × ")

  const compactInfo = [
    dimensionsSummary,
    formatWeightCompact(config.weight),
    config.maxSpeed
      ? t("maps.collapsibleVehicleTollInfo.maxSpeedCompact", {
          speed: config.maxSpeed,
        })
      : null,
    config.fuelConsumption ? `${config.fuelConsumption} l/100km` : null,
  ]
    .filter(Boolean)
    .join(" • ")

  return (
    <div
      className={cn(
        "rounded-md border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800",
        className,
      )}
    >
      {/* Collapsed header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        // Phone: one tight line (the dimensions truncate rather than wrap),
        // so the card costs ~36px instead of ~75px of a half-height panel.
        className="flex w-full items-center justify-between gap-2 p-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 md:gap-3 md:p-3"
        title={compactInfo}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
          <Truck className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
          <span className="truncate text-xs md:text-sm">{compactInfo}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          {showFuel && fuel ? (
            <div className="flex items-center gap-1 md:gap-1.5">
              <Fuel className="h-4 w-4 md:h-5 md:w-5" />
              <span className="whitespace-nowrap text-xs md:text-sm">
                {Math.round(fuel)} l
              </span>
            </div>
          ) : null}
          {totalTollDisplay && (
            <div className="flex items-center gap-1 md:gap-1.5">
              <Ticket className="h-4 w-4 md:h-5 md:w-5" />
              <span className="whitespace-nowrap text-xs md:text-sm">
                {totalTollDisplay}
              </span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-6">
            {/* Truck info column */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                {t("maps.collapsibleVehicleTollInfo.truckInfo")}
              </h3>
              <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.length")}
                  </span>
                  <span>{formatValue(config.length, "m")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.width")}
                  </span>
                  <span>{formatValue(config.width, "m")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.height")}
                  </span>
                  <span>{formatValue(config.height, "m")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.weight")}
                  </span>
                  <span>{formatValue(config.weight, "kg")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.maxSpeed")}
                  </span>
                  <span>{formatValue(config.maxSpeed, "km/h")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.travelMode")}
                  </span>
                  <span>{formatOption(config.travelMode)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.axleCount")}
                  </span>
                  <span>{config.axleCount || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.co2Class")}
                  </span>
                  <span>{formatOption(config.co2class)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t("maps.collapsibleVehicleTollInfo.emissionType")}
                  </span>
                  <span>{formatOption(config.emissionType)}</span>
                </div>
                {showFuel &&
                  (onFuelConsumptionChange ? (
                    <div className="flex items-center justify-between gap-2">
                      {/* Unit lives in the label so the row fits the narrow column */}
                      <label
                        htmlFor="plan-route-fuel-consumption"
                        className="whitespace-nowrap font-medium"
                      >
                        {t("maps.collapsibleVehicleTollInfo.fuelInput")}
                      </label>
                      <Input
                        id="plan-route-fuel-consumption"
                        type="number"
                        inputMode="decimal"
                        min={0.1}
                        max={MAX_FUEL_CONSUMPTION}
                        step="any"
                        value={fuelDraft}
                        onChange={(e) => setFuelDraft(e.target.value)}
                        onBlur={commitFuelDraft}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault()
                            commitFuelDraft()
                          }
                        }}
                        placeholder="—"
                        className="h-5 w-14 rounded px-1 py-0 text-right text-xs shadow-none [appearance:textfield] md:text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <span className="font-medium">
                        {t("maps.collapsibleVehicleTollInfo.fuelConsumption")}
                      </span>
                      <span>
                        {formatValue(config.fuelConsumption, "l/100km")}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {/* Tolls details column */}
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                {t("maps.collapsibleVehicleTollInfo.tollsDetails")}
              </h3>
              {tolls && tolls.length > 0 ? (
                <div className="space-y-1.5 text-xs">
                  {Object.entries(tollsByCountry)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([countryCode, currencyMap]) => (
                      <div
                        key={countryCode}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm">
                            {getCountryFlag(countryCode)}
                          </span>
                          <span className="text-gray-600 dark:text-gray-300">
                            {getCountryName(countryCode)}
                          </span>
                        </div>
                        <div className="text-right">
                          {Object.entries(currencyMap).map(
                            ([currency, amount]) => (
                              <div
                                key={`${countryCode}-${currency}`}
                                className="font-medium text-gray-900 dark:text-white"
                              >
                                {formatCurrency(amount, currency)}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    ))}
                  {/* Total row */}
                  <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {t("maps.collapsibleVehicleTollInfo.total")}
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {Object.entries(totalByCurrency)
                          .map(([currency, amount]) =>
                            formatCurrency(amount, currency),
                          )
                          .join(" + ")}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t("maps.collapsibleVehicleTollInfo.noTolls")}
                </p>
              )}

              {showFuel && fuel ? (
                <div className="mt-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                    {t("maps.collapsibleVehicleTollInfo.fuel")}
                  </h3>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t(
                        "maps.collapsibleVehicleTollInfo.estimatedConsumption",
                      )}
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {fuel.toFixed(1)} l
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
